<template>
  <div class="app-main">
    <!--
      Every seat on one strip, the active one lit. Unlike the sibling games
      there is no hidden hand here - the board below IS everyone's state - so
      the strip only carries who's who, claim counts, and whose turn it is.
    -->
    <!-- Async room: who the game is waiting on (src/store/asyncPlay.js). -->
    <p v-if="waitingBanner" class="waiting-banner" :class="{ 'is-mine': waitingOnMe }">{{ waitingBanner }}</p>
    <section class="seats-strip">
      <div
        v-for="player in seatsInOrder"
        :key="player.id"
        class="seat-chip"
        :class="{ 'seat-chip--active': player.id === activePlayer?.id }"
      >
        <span class="seat-chip__dot" :style="{ background: colorOf(player) }" />
        <span class="seat-chip__name">
          {{ player.name }}
          <span v-if="isAbsent(player)" class="seat-gone" title="Disconnected - a bot is playing this hand">away</span>
        </span>
        <span class="seat-chip__claims" :title="`${claimsOf(player)} of 3 columns claimed`">
          <i
            v-for="n in 3"
            :key="n"
            class="claim-pip"
            :class="{ 'claim-pip--won': n <= claimsOf(player) }"
          />
        </span>
        <span v-if="player.id === thinkingPlayerId" class="thinking-dots">
          <span>.</span><span>.</span><span>.</span>
        </span>
      </div>
    </section>

    <section class="table-centre">
      <GameBoard :game="game" />
    </section>

    <section class="you">
      <div class="you__top">
        <span class="you__label">{{ turnNotice }}</span>
        <span v-if="stepsThisTurn > 0" class="you__score">
          <strong>{{ stepsThisTurn }}</strong> step{{ stepsThisTurn === 1 ? '' : 's' }} at risk
        </span>
      </div>

      <!--
        The tray holds ONE stable shape for the whole turn. Bug report (Matt,
        2026-08-28): "the space that says busted and the space where it shows
        the dice pop in and out of existence based on conditions and it makes
        the whole board jumpy and not feel very stable and nice."

        They did: the dice row only existed once you'd rolled, the options
        only while choosing, the bust banner only on a bust - so the tray
        changed height three times a turn and the buttons walked up and down
        the screen under your thumb. Now the dice are always drawn (blank
        before the first roll) and the row beneath them is one slot with a
        floor, holding whichever of options / bust / runner summary applies.
      -->
      <div class="tray">
        <!--
          The roll (Matt, 2026-08-29, two rounds of notes): real 3D cubes
          that tumble to their values - no shake-up prelude ("looks like a
          glitch"), just the tumble, long, and EACH DIE ON ITS OWN CLOCK.
          The options / bust banner hold back until the last die lands - the
          suspense IS the game, and the tray-slot's fixed floor means the
          wait can't move anything. Reduced-motion users get the result
          immediately; see startTumble().
        -->
        <div class="dice-row" :class="{ 'dice-row--bust': isBusted && !tumbling }">
          <Die3d
            v-for="n in 4"
            :key="n"
            :value="displayDice ? displayDice[n - 1] : 1"
            :phase="rollPhase"
            :duration="dieDurations[n - 1]"
            :placeholder="!displayDice"
            :dim="Boolean(displayDice) && !tumbling && !choosing && !isBusted"
          />
        </div>

        <div class="tray-slot">
          <!-- The choice a roll poses: which pairing to play. -->
          <div v-if="choosing && myTurn && !tumbling" class="options">
            <button
              v-for="(option, index) in game.turn.options"
              :key="index"
              class="option"
              :aria-label="`advance ${option.sums.join(' and ')}`"
              @click="choose(index)"
            >
              <span class="option__sums">
                <template v-for="(sum, i) in option.sums" :key="i">
                  <span v-if="i > 0" class="option__amp">&amp;</span>
                  <span class="option__sum" :class="{ 'option__sum--new': isNewColumn(sum, option, i) }">{{ sum }}</span>
                </template>
              </span>
              <small>{{ optionCaption(option) }}</small>
            </button>
          </div>

          <!-- The bust. The whole game in one red banner. -->
          <div v-else-if="isBusted && !tumbling" class="bust-banner">
            <strong>Busted!</strong>
            <span v-if="myTurn">No way to play that roll — this turn's progress is gone.</span>
            <span v-else>{{ activePlayer?.name }} can't play that roll.</span>
          </div>

          <!-- Otherwise the slot earns its keep rather than sitting blank:
               where the three runners are, which is the thing you weigh when
               deciding whether to roll again. -->
          <p v-else class="tray-runners">{{ runnerSummary }}</p>
        </div>
      </div>

      <div class="actions">
        <template v-if="isBusted && !tumbling">
          <!-- The subtitle is not decoration: the other two buttons carry one,
               and without it this row is shorter, which walks the whole tray
               15px down the screen on a bust. Same reason as the tray slot
               above - nothing in the action area may move. -->
          <button class="action action--forced" :disabled="!myTurn" @click="ackBust">
            Pass the dice
            <small>this turn is over</small>
          </button>
        </template>
        <template v-else>
          <button
            class="action action--stop"
            :disabled="!myTurn || choosing || tumbling || !canStop"
            @click="stopTurn"
          >
            Stop
            <small>{{ stopSubtitle }}</small>
          </button>
          <button
            class="action action--roll"
            :disabled="!myTurn || choosing || tumbling || !canRoll"
            @click="rollDice"
          >
            {{ firstRoll ? 'Roll' : 'Roll again' }}
            <small>{{ choosing ? 'pick a move first' : 'all four dice' }}</small>
          </button>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { waitingLine, waitingOnMe as isWaitingOnMe } from '@/store/asyncPlay.js';
import { COLUMN_HEIGHTS, TURN_PHASES } from '@/engine/constants';
import { claimedCount } from '@/engine/actions/turnActions';
import GameBoard, { SEAT_COLORS } from './GameBoard.vue';
import Die3d from './Die3d.vue';
import { dealTumbleDurations, rollAnimationMs } from '@/utils/rollTheatre';

// The roll's timings live in utils/rollTheatre.js, shared with the store:
// durations are deterministic per roll, so the bot pacing can hug the
// actual landing instead of padding for the worst case. See that module.

export default {
  name: 'GameTable',
  components: { GameBoard, Die3d },
  data() {
    return {
      // What the dice row shows. The values go in as the tumble starts -
      // unreadable while the cubes spin, and the final half-turn is the
      // reveal.
      displayDice: null,
      // null | 'tumble' - Die3d renders whatever this says.
      rollPhase: null,
      // Each die's own tumble length for the current roll, dealt by
      // startTumble - the component knows its die, this knows the CLOCK
      // (the gate lifts when the slowest one lands).
      dieDurations: [1600, 1600, 1600, 1600],
      tumbleTimer: null,
      // True from roll start until a beat after the dice land - see
      // startTumble. Kept separate from rollPhase so the cubes can be at
      // rest while the options still hold back for the reveal beat.
      settleHold: false,
    };
  },
  watch: {
    // A roll is detected from the LOG, not by watching the dice array - the
    // engine's clone() gives turn.dice a fresh reference on every action, so
    // the array reference changes even when nobody rolled.
    'game.meta.seq': {
      immediate: true,
      handler() {
        if (!this.game) return;
        const last = this.game.log[this.game.log.length - 1];
        if (last && (last.kind === 'roll' || last.kind === 'bust') && Array.isArray(last.dice)) {
          this.startTumble(this.game.turn.dice || last.dice);
        } else {
          // Any other action (choose, stop, pass): mirror the state directly.
          this.stopTumble();
          this.displayDice = this.game.turn.dice ? [...this.game.turn.dice] : null;
        }
      },
    },
  },
  beforeUnmount() {
    this.stopTumble();
  },
  computed: {
    ...mapState(['game', 'thinkingPlayerId', 'presence', 'roomId']),
    ...mapGetters([
      'activePlayer', 'canRoll', 'canStop', 'isHumanTurn',
      'isOnline', 'isMyOnlineTurn', 'myUid',
    ]),
    /**
     * Online, "me" is the seat carrying my own uid; offline it's the one
     * non-bot seat.
     */
    human() {
      if (this.isOnline) {
        return this.game.players.find((p) => p.id === this.myUid) || null;
      }
      return this.$store.getters.humanPlayer;
    },
    // Everyone, in seat order starting from me - so the strip reads the way
    // the dice will travel.
    seatsInOrder() {
      const me = this.human;
      const { players } = this.game;
      const start = me ? me.seat : 0;
      return players.map((_, i) => players[(start + i) % players.length]);
    },
    waitingOnMe() { return isWaitingOnMe(this.$store.state.roomMeta, this.myUid); },
    waitingBanner() {
      const meta = this.$store.state.roomMeta;
      if (!meta?.async || !this.game) return '';
      const nameOf = (id) => this.game.players.find((p) => p.id === id)?.name || 'someone';
      return waitingLine(meta, this.myUid, nameOf);
    },
    myTurn() {
      return this.isOnline ? this.isMyOnlineTurn : this.isHumanTurn;
    },
    choosing() { return this.game.turn.phase === TURN_PHASES.CHOOSING; },
    // The single flag every gate reads: options, bust banner, labels and
    // buttons all wait until the roll has fully played out.
    tumbling() { return this.rollPhase !== null || this.settleHold === true; },
    isBusted() { return this.game.turn.phase === TURN_PHASES.BUSTED; },
    firstRoll() { return !this.game.turn.moved && !this.game.turn.dice; },
    turnNotice() {
      // While the dice tumble the outcome is a secret the label must keep -
      // 'oh no' half a second early would spoil the one dramatic beat.
      if (this.tumbling) return 'rolling…';
      if (this.isBusted && this.myTurn) return 'oh no';
      if (this.myTurn) return this.choosing ? 'pick a move' : 'your turn';
      const active = this.activePlayer;
      return active ? `${active.name}'s turn` : '';
    },
    /**
     * Raw steps the runners have gained this turn - the thing a bust takes
     * back. Arithmetic on the board everyone can see, so showing it removes
     * bookkeeping, not judgement. The bust ODDS stay unshown on purpose:
     * feeling those out is the entire game, and printing them would play it
     * for you.
     */
    stepsThisTurn() {
      if (!this.myTurn && !this.activePlayer) return 0;
      const player = this.activePlayer;
      if (!player) return 0;
      return Object.entries(this.game.turn.runners || {}).reduce((total, [column, step]) => (
        total + step - (player.markers?.[column] || 0)
      ), 0);
    },
    pendingClaims() {
      return Object.entries(this.game.turn.runners || {})
        .filter(([column, step]) => step >= COLUMN_HEIGHTS[Number(column)]).length;
    },
    stopSubtitle() {
      if (this.pendingClaims === 1) return 'bank it — claims a column!';
      if (this.pendingClaims > 1) return `bank it — claims ${this.pendingClaims} columns!`;
      return 'bank this progress';
    },
    /**
     * What fills the tray slot when there is nothing to choose and nothing
     * has busted. Says where the runners are and how many are left, which is
     * exactly the state you're weighing when the question is roll-or-stop.
     */
    runnerSummary() {
      const columns = Object.keys(this.game.turn.runners || {}).map(Number).sort((a, b) => a - b);
      if (columns.length === 0) {
        return this.myTurn ? 'Three runners ready' : 'No runners out yet';
      }
      const left = 3 - columns.length;
      const where = `Runner${columns.length > 1 ? 's' : ''} on ${columns.join(', ')}`;
      if (left === 0) return `${where} — all three placed`;
      return `${where} — ${left} still free`;
    },
  },
  methods: {
    /**
     * The roll. Each die is dealt its own tumble length, all four launch
     * together, and they land one by one - the gate lifts a beat after the
     * slowest one settles, so the reveal is SEEN before the options crowd
     * it. Purely presentation: the engine rolled the moment the action
     * dispatched, and nothing here can change what it rolled.
     * Reduced-motion preference skips straight to the result.
     */
    startTumble(finalDice) {
      this.stopTumble();
      const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (still) {
        this.displayDice = [...finalDice];
        return;
      }
      // Deterministic per roll (dice + seq), so the store's bot pacing and
      // every other client watching this game compute the same landing.
      this.dieDurations = dealTumbleDurations(finalDice, this.game.meta.seq);
      this.settleHold = true;
      this.displayDice = [...finalDice];
      this.rollPhase = 'tumble';
      this.tumbleTimer = window.setTimeout(() => {
        this.rollPhase = null;
        this.settleHold = false;
        this.tumbleTimer = null;
      }, rollAnimationMs(this.game));
    },
    stopTumble() {
      if (this.tumbleTimer) window.clearTimeout(this.tumbleTimer);
      this.tumbleTimer = null;
      this.rollPhase = null;
      this.settleHold = false;
    },
    colorOf(player) { return SEAT_COLORS[player.seat % SEAT_COLORS.length]; },
    claimsOf(player) { return claimedCount(this.game, player.id); },
    isAbsent(player) {
      return this.isOnline && !player.isBot && !this.presence[player.id];
    },
    isNewColumn(sum, option, i) {
      // Highlight sums that would place a fresh runner (spending one of the
      // three) - the fact most worth noticing when choosing.
      const alreadyRunning = this.game.turn.runners?.[sum] !== undefined;
      if (alreadyRunning) return false;
      // Second use of a doubled sum rides the runner the first use placed.
      return option.sums.indexOf(sum) === i;
    },
    optionCaption(option) {
      const { runners } = this.game.turn;
      const fresh = new Set(
        option.sums.filter((sum, i) => runners?.[sum] === undefined && option.sums.indexOf(sum) === i),
      );
      if (fresh.size === 0) return 'advance';
      const used = Object.keys(runners || {}).length;
      const left = 3 - used - fresh.size;
      return left <= 0 ? 'places your last runner' : `new runner${fresh.size > 1 ? 's' : ''}`;
    },
    rollDice() { this.$store.dispatch('roll'); },
    stopTurn() { this.$store.dispatch('stop'); },
    ackBust() { this.$store.dispatch('ackBust'); },
    choose(index) { this.$store.dispatch('chooseMove', index); },
  },
};
</script>

<style scoped>
.waiting-banner { grid-column: 1 / -1; margin: 0; padding: 0.5rem 0.9rem; border-radius: 0.6rem; background: rgba(0,0,0,0.08); font-size: 0.95rem; }
.waiting-banner.is-mine { background: rgba(0,0,0,0.16); font-weight: 700; }
</style>